import types from '../mutation-types';
import KanbanAPI from '../../api/kanban';
import KanbanConfigAPI from '../../api/kanbanConfig';


const state = {
  isEnabled: false,
  items: [],
  itemsMap: new Map(), // Para buscas O(1)
  loading: false,
  error: null,
  config: {
    title: 'Kanban',
    defaultView: 'kanban',
    webhookUrl: '',
    webhookSecret: '',
    webhookEnabled: false,
    webhookEvents: [],
    autoAssignment: false,
    notificationsEnabled: false,
    supportEmail: '',
    supportPhone: '',
    supportChatHours: '',
    supportChatEnabled: false,
    dragbarEnabled: true,
    quickMessageEnabled: false,
    listViewEnabled: true,
    agendaViewEnabled: true,
  },
  pagination: {
    currentPage: 1,
    hasMore: true,
    loadingMore: false,
  },
};

const getters = {
  isKanbanEnabled: _state => _state.isEnabled,
  getKanbanItems: _state => _state.items,
  isLoading: _state => _state.loading,
  isLoadingMore: _state => _state.pagination.loadingMore,
  getError: _state => _state.error,
  getKanbanConfig: _state => _state.config,

  getPaginationInfo: _state => _state.pagination,
  hasMorePages: _state => _state.pagination.hasMore,
  // Busca eficiente O(1)
  getItemById: _state => id => _state.itemsMap.get(Number(id)),

  // Getters específicos para propriedades de configuração
  getKanbanTitle: _state => _state.config.title,
  getWebhookUrl: _state => _state.config.webhookUrl,
  getWebhookEnabled: _state => _state.config.webhookEnabled,
  getDragbarEnabled: _state => _state.config.dragbarEnabled,
  getQuickMessageEnabled: _state => _state.config.quickMessageEnabled,
  getListViewEnabled: _state => _state.config.listViewEnabled !== false,
  getAgendaViewEnabled: _state => _state.config.agendaViewEnabled !== false,
};

const actions = {
  setKanbanEnabled({ commit }, isEnabled) {
    commit(types.SET_KANBAN_ENABLED, isEnabled);
  },

  async fetchKanbanItems({ commit, getters }, { force = false, stageId = null, page = 1 } = {}) {
    if (getters.isLoading && !force) return;

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    // Reset paginação apenas no force ou primeira página
    if (force || page === 1) {
      commit('RESET_PAGINATION');
    }

    try {
      const funnelId = getters['funnel/getSelectedFunnel']?.id;
      if (!funnelId) throw new Error('Nenhum funil selecionado');

      const response = await KanbanAPI.getItems(funnelId, page, stageId);
      const items = response.data.items || response.data;
      const pagination = response.data.pagination;

      // Atualização baseada no tipo de operação
      if (stageId) {
        commit('MERGE_STAGE_ITEMS', { items, stageId });
      } else if (page > 1) {
        commit('APPEND_ITEMS', items);
      } else {
        commit('SET_KANBAN_ITEMS', items);
      }

      // Atualizar paginação
      if (pagination) {
        commit('SET_PAGINATION_INFO', {
          currentPage: pagination.current_page || page,
          hasMore: pagination.has_more || items.length > 0,
        });
      }

      return items;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchMoreKanbanItems({ dispatch, getters }, { stageId = null } = {}) {
    if (!getters.hasMorePages || getters.isLoadingMore) return;

    const nextPage = getters.getPaginationInfo.currentPage + 1;
    return dispatch('fetchKanbanItems', { stageId, page: nextPage });
  },

  async createKanbanItem({ commit }, payload) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.createItem(payload);
      commit('ADD_KANBAN_ITEM', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async moveItemToStage({ commit, getters, dispatch }, { itemId, columnId, funnelId = null }) {
    const numericItemId = Number(itemId);
    const originalItem = getters.getItemById(numericItemId);


    try {
      const updatedItem = await KanbanAPI.moveToStage(numericItemId, columnId, funnelId);

      if (updatedItem) {
        commit('UPDATE_ITEM_WITH_RESPONSE', updatedItem);

        // 🔥 REMOVIDO: Refresh forçado estava causando perda de dados
        // Deixe as colunas se sincronizarem automaticamente via watchers

        return updatedItem;
      } else {
        throw new Error('API moveToStage retornou dados inválidos');
      }
    } catch (error) {
      // Não setar erro no state para não exibir na UI
      // Apenas re-lançar para ser capturado pelo componente
      throw error;
    }
  },

  async deleteKanbanItem({ commit, getters }, itemId) {
    const originalItem = getters.getItemById(itemId);
    commit('REMOVE_KANBAN_ITEM', itemId);

    try {
      await KanbanAPI.deleteItem(itemId);
      return true;
    } catch (error) {
      if (originalItem) {
        commit('ADD_KANBAN_ITEM', originalItem);
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async updateItemStructure({ commit, dispatch }, { itemId, structure }) {
    commit('SET_LOADING', true);
    try {
      const response = await KanbanAPI.updateItemStructure(itemId, {
        item: { item_structure: structure },
      });
      commit('UPDATE_ITEM_STRUCTURE', {
        itemId,
        structure: response.data.item_structure,
      });
      // Item atualizado com sucesso
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateItemDetails({ commit }, { itemId, details }) {
    // Primeiro, obter o item atual para possível rollback
    const originalItem = getters.getItemById(itemId);

    // Atualizar o store local com os dados enviados
    commit('UPDATE_ITEM_DETAILS', { itemId, details });

    try {
      const response = await KanbanAPI.updateItem(itemId, details);

      // Sincronizar com a resposta da API (pode conter campos calculados pelo backend)
      if (response.data) {
        commit('UPDATE_ITEM_DETAILS', { itemId, details: response.data });
      }

      return response.data;
    } catch (error) {
      // Rollback em caso de erro
      if (originalItem) {
        commit('UPDATE_ITEM_DETAILS', { itemId, details: originalItem });
      }
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async registerActivity({ commit }, { itemId, type, details }) {
    try {
      const currentItem = await KanbanAPI.getItem(itemId);
      const newActivity = {
        id: Date.now(),
        type,
        details,
        created_at: new Date().toISOString(),
        user: this.getters.getCurrentUser,
      };

      const activities = [...(currentItem.data.activities || []), newActivity];
      const payload = {
        ...currentItem.data,
        activities,
        item_details: {
          ...currentItem.data.item_details,
        },
      };

      const response = await KanbanAPI.updateItem(itemId, payload);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message);
      throw error;
    }
  },

  async fetchKanbanConfig({ commit }) {
    try {
      const response = await KanbanConfigAPI.getConfig();
      const config = {
        title: response.data.config?.title || 'Kanban',
        defaultView: response.data.config?.default_view || 'kanban',
        webhookUrl: response.data.webhook_url || '',
        webhookSecret: response.data.webhook_secret || '',
        webhookEnabled: response.data.enabled || false,
        webhookEvents: response.data.webhook_events || [],
        autoAssignment: response.data.config?.auto_assignment || false,
        notificationsEnabled: response.data.config?.notifications_enabled || false,
        supportEmail: response.data.config?.support_email || '',
        supportPhone: response.data.config?.support_phone || '',
        supportChatHours: response.data.config?.support_chat_hours || '',
        supportChatEnabled: response.data.config?.support_chat_enabled || false,
        dragbarEnabled: response.data.config?.dragbar_enabled !== false,
        quickMessageEnabled: response.data.config?.quick_message_enabled || false,
        listViewEnabled: response.data.config?.list_view_enabled !== false,
        agendaViewEnabled: response.data.config?.agenda_view_enabled !== false,
      };
      commit('SET_KANBAN_CONFIG', config);
      return config;
    } catch (error) {
      const defaultConfig = {
        title: 'Kanban',
        defaultView: 'kanban',
        webhookUrl: '',
        webhookSecret: '',
        webhookEnabled: false,
        webhookEvents: [],
        autoAssignment: false,
        notificationsEnabled: false,
        supportEmail: '',
        supportPhone: '',
        supportChatHours: '',
        supportChatEnabled: false,
        dragbarEnabled: true,
        quickMessageEnabled: false,
        listViewEnabled: true,
        agendaViewEnabled: true,
      };
      commit('SET_KANBAN_CONFIG', defaultConfig);
      return defaultConfig;
    }
  },

  updateLocalItems({ commit }, items) {
    commit('SET_KANBAN_ITEMS', items);
  },

  async saveKanbanConfig({ commit, dispatch }, configData) {
    try {
      const response = await KanbanConfigAPI.saveConfig(configData);

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      throw error;
    }
  },

  async updateWebhookConfig({ commit, dispatch }, webhookData) {
    try {
      const response = await KanbanConfigAPI.updateWebhookConfig(webhookData);

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      throw error;
    }
  },

  async testWebhook({ commit }) {
    try {
      const response = await KanbanConfigAPI.testWebhook();
      return response;
    } catch (error) {
      throw error;
    }
  },

  async updateQuickMessageConfig({ commit, dispatch, state: localState }, enabled) {
    try {
      // Obter a configuração atual do store
      const currentConfig = { ...localState.config };

      // Fazer merge da nova configuração com a existente
      const updatedConfig = {
        ...currentConfig,
        quick_message_enabled: enabled
      };

      const response = await KanbanConfigAPI.updateConfig({
        config: updatedConfig
      });

      // Recarregar configurações após salvar
      await dispatch('fetchKanbanConfig');

      return response;
    } catch (error) {
      throw error;
    }
  },


};

const mutations = {
  [types.SET_KANBAN_ENABLED](_state, isEnabled) {
    _state.isEnabled = isEnabled;
  },
  SET_LOADING(_state, loading) {
    _state.loading = loading;
  },
  SET_ERROR(_state, error) {
    _state.error = error;
  },

  // Items com Map para buscas O(1)
  SET_KANBAN_ITEMS(_state, items) {
    _state.items = items || [];
    _state.itemsMap.clear();
    items.forEach(item => _state.itemsMap.set(item.id, item));
  },

  ADD_KANBAN_ITEM(_state, item) {
    _state.items.push(item);
    _state.itemsMap.set(item.id, item);
  },

  REMOVE_KANBAN_ITEM(_state, itemId) {
    _state.items = _state.items.filter(item => item.id !== itemId);
    _state.itemsMap.delete(itemId);
  },

  // Mutations simplificadas
  MERGE_STAGE_ITEMS(_state, { items, stageId }) {
    const existingIds = new Set(_state.items.map(item => item.id));
    const newItems = items.filter(item => !existingIds.has(item.id));

    if (newItems.length > 0) {
      _state.items.push(...newItems);
      newItems.forEach(item => _state.itemsMap.set(item.id, item));
    }
  },

  APPEND_ITEMS(_state, items) {
    const existingIds = new Set(_state.items.map(item => item.id));
    const newItems = items.filter(item => !existingIds.has(item.id));

    if (newItems.length > 0) {
      _state.items.push(...newItems);
      newItems.forEach(item => _state.itemsMap.set(item.id, item));
    }
  },
  UPDATE_ITEM_STAGE(_state, { itemId, columnId }) {
    const item = _state.itemsMap.get(Number(itemId));
    if (item) {
      item.funnel_stage = columnId;
      _state.itemsMap.set(item.id, item);
    }
  },
  UPDATE_ITEM_WITH_RESPONSE(_state, updatedItem) {
    const itemId = Number(updatedItem.id);
    const existingItemIndex = _state.items.findIndex(item => item.id === itemId);

    if (existingItemIndex >= 0) {
      // Atualizar item existente
      _state.items.splice(existingItemIndex, 1, { ...updatedItem });
    } else {
      // Adicionar item novo se não existir
      _state.items.push(updatedItem);
    }

    // Atualizar Map
    _state.itemsMap.set(itemId, updatedItem);
  },
  UPDATE_ITEM_STRUCTURE(_state, { itemId, structure }) {
    const item = _state.itemsMap.get(Number(itemId));
    if (item) {
      item.item_structure = structure;
      _state.itemsMap.set(item.id, item);
    }
  },

  UPDATE_ITEM_DETAILS(_state, { itemId, details }) {
    const itemIdNum = Number(itemId);
    let item = _state.itemsMap.get(itemIdNum);

    // Se não encontrou no Map, procurar no array
    if (!item) {
      item = _state.items.find(i => i.id === itemIdNum);
    }

    if (item) {
      // Atualizar todas as propriedades do item
      Object.keys(details).forEach(key => {
        if (key === 'item_details' && details.item_details) {
          item.item_details = { ...item.item_details, ...details.item_details };
        } else if (key in item || key in details) {
          item[key] = details[key];
        }
      });

      // Garantir que o item tenha ID correto
      item.id = itemIdNum;

      // Sincronizar tanto o array quanto o Map
      _state.itemsMap.set(itemIdNum, item);

      // Atualizar também no array items se existir
      const itemIndex = _state.items.findIndex(i => i.id === itemIdNum);
      if (itemIndex >= 0) {
        _state.items[itemIndex] = item;
      }
    }
  },
  SET_KANBAN_CONFIG(_state, config) {
    _state.config = config;
  },

  RESET_PAGINATION(_state) {
    _state.pagination = {
      currentPage: 1,
      hasMore: true,
      loadingMore: false,
    };
  },
  SET_PAGINATION_INFO(_state, { currentPage, hasMore }) {
    _state.pagination.currentPage = currentPage;
    _state.pagination.hasMore = hasMore;
  },
  SET_LOADING_MORE(_state, loading) {
    _state.pagination.loadingMore = loading;
  },

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
